import { FC, useRef, useState, useEffect } from 'react';
import Loader from 'components/Loader';
import Spacing from 'components/Spacing';
import { Tickets } from 'components/Tickets';
import { CandidateCard } from 'components/Cards/CandidateCard';
import { getUrlParams } from 'helpers/utils';
import { IPageType } from 'store/donatePage/types';
import { Candidates, CandidateType } from './Candidates/Candidates';
import { ContributionRules } from './ContributionRules';
import { useAppSelector } from 'helpers/hooks';
import { getRecipientsState } from 'selectors/recipients';
import Icon, { IconNames } from 'components/Icon';
import { PopupModal } from 'components/PopupModal';
import { IEvent, TicketWithQuantitySelected } from 'store/eventPage/types';
import {
  clearCheckoutIdempotencyKey,
  clearEventPageSelections,
  setEventPageSelections,
} from 'services/storage';
import {
  clearEventFromStorageIfNeeded,
  getCustomAmountFromTicketSelections,
  getEventSelectionsObj,
  getTicketPriceTotal,
  getTicketSelectionsFromEventData,
} from 'helpers/event';
import { ProcessingRedirectOptions } from 'store/donatePage/constants';
import { CustomSplit } from './CustomSplit';
import { Toast } from 'components/Toast';

interface Props {
  type: IPageType;
  candidates: CandidateType[];
  eventData: IEvent;
  showCustomSplit: boolean;
  selectedDonationAmount?: number;
  overageMax?: number;
  ticketSelections: TicketWithQuantitySelected[];
  handleCandidatesSelected: (index: number, checkValue: boolean) => void;
  onDonateClick: () => void;
  onSelectCandidates: (updatedCandidates: CandidateType[]) => void;
  onUserDonationTotalChange: (value?: number) => void;
  setCandidates: React.Dispatch<React.SetStateAction<CandidateType[]>>;
  setSelectedDonationAmount: React.Dispatch<React.SetStateAction<number | undefined>>;
  setTicketSelections: React.Dispatch<React.SetStateAction<TicketWithQuantitySelected[]>>;
}

export const EventSlate: FC<Props> = ({
  type,
  candidates,
  eventData,
  selectedDonationAmount,
  overageMax,
  ticketSelections,
  showCustomSplit,
  handleCandidatesSelected,
  onDonateClick,
  onSelectCandidates,
  onUserDonationTotalChange,
  setCandidates,
  setSelectedDonationAmount,
  setTicketSelections,
}) => {
  const ref = useRef(null);
  const { urlPartner } = getUrlParams();
  const recipients = useAppSelector(getRecipientsState);
  const [customAmount, setCustomAmount] = useState(0);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const saveEventPageSelectionsToStorage = () => {
    const eventPageSelections = {
      event_tickets: ticketSelections,
      custom_amount: customAmount,
      event_id: eventData.event_id,
    };

    clearEventPageSelections();
    const eventPageSelectionsString = JSON.stringify(eventPageSelections);
    setEventPageSelections(eventPageSelectionsString);
  };

  const getEventPageSelectionsFromStorage = () => {
    const eventSelections = getEventSelectionsObj();
    if (eventSelections) {
      setCustomAmount(eventSelections.custom_amount || 0);
      setTicketSelections(eventSelections.event_tickets || []);
    }
  };

  const onEventPageDonateClick = () => {
    // we only need to keep this in session storage for DE, stripe checkout uses Main component state
    if (eventData?.processing_redirect !== ProcessingRedirectOptions.STRIPE) {
      saveEventPageSelectionsToStorage();
    }

    onDonateClick();
  };

  useEffect(() => {
    if (!eventData) {
      return;
    }

    clearEventFromStorageIfNeeded(eventData);

    if (ticketSelections.length > 0) {
      setCustomAmount(
        getCustomAmountFromTicketSelections(selectedDonationAmount || 0, ticketSelections)
      );
    } else {
      setTicketSelections(getTicketSelectionsFromEventData(eventData));
    }

    clearCheckoutIdempotencyKey();

    if (eventData?.processing_redirect !== ProcessingRedirectOptions.STRIPE) {
      getEventPageSelectionsFromStorage();
    }
  }, [eventData]);

  useEffect(() => {
    const newAmount = customAmount + getTicketPriceTotal(ticketSelections);
    setSelectedDonationAmount(newAmount);
    onUserDonationTotalChange(newAmount);
  }, [customAmount, ticketSelections]);

  return (
    <div className="slate-content">
      <Toast />
      <div className="event-slate-container">
        <PopupModal
          autoClose={false}
          closePopup={() => setIsPopupOpen(false)}
          isSelected={isPopupOpen}
          title="What’s the Impact Score?"
          description="Oath’s Impact Score is calculated from dozens of data points across three metrics: Competitiveness, Stakes, and Financial Need."
        />
        {eventData && (
          <>
            <h4 className="h4-text">Select your Ticket Option</h4>
            <p className="body-small-text color-gray">
              Once you select your ticket options, we will direct you to the donation checkout.
            </p>
            <Tickets
              ticketSelections={ticketSelections}
              setTicketSelections={setTicketSelections}
              customAmount={customAmount}
              setCustomAmount={setCustomAmount}
            />
            <button
              className="button-brand"
              disabled={!selectedDonationAmount || !candidates?.find(c => c.isSelected)}
              onClick={onEventPageDonateClick}
            >
              Proceed to checkout
            </button>
            <hr className="event-slate-hr" />
            <CustomSplit
              allocationType={eventData.allocation_type}
              candidates={candidates}
              donationTotal={selectedDonationAmount}
              overageMax={overageMax}
              recipientGroups={eventData.recipient_groups}
              resetDonationTotal={onUserDonationTotalChange}
              setCandidates={setCandidates}
              setDonationTotal={setSelectedDonationAmount}
              showCustomSplit={showCustomSplit}
            />
          </>
        )}
        {urlPartner && type === 'COMPACT' ? (
          <div className="event-slate-candidates-container">
            <Candidates
              allocationType={eventData.allocation_type}
              candidates={candidates}
              recipientCount={recipients?.data?.recipients.length}
              setCandidates={onSelectCandidates}
            />
          </div>
        ) : (
          candidates?.length > 0 && (
            <>
              {recipients.isLoading ? (
                <div className="loader-center">
                  <Loader width={60} height={60} />
                </div>
              ) : (
                <>
                  {eventData?.impact_score_visibility && (
                    <div className="event-slate-recipients-header">
                      <p className="body-text color-black">Recipients</p>
                      <div className="event-slate-impact-score-cell">
                        <p className="body-text color-black">Impact Score</p>
                        <Spacing $isVertical $size={8} />
                        <Icon
                          name={IconNames.HelpCircle}
                          size={20}
                          style={{ cursor: 'pointer' }}
                          onClick={() => setIsPopupOpen(true)}
                        />
                      </div>
                    </div>
                  )}
                  <Spacing $size={8} />
                  {candidates?.map((item, i) => (
                    <CandidateCard
                      key={i}
                      index={i}
                      item={item}
                      variant="page"
                      selectItem={handleCandidatesSelected}
                      impactScoreVisibility={
                        (urlPartner && eventData?.impact_score_visibility) || false
                      }
                      showMoreVisibility={urlPartner ? eventData?.show_more_visibility : true}
                    />
                  ))}
                </>
              )}
            </>
          )
        )}
        <div ref={ref} />
        <Spacing $size={16} />
        <ContributionRules />
      </div>
    </div>
  );
};
